package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.codegen.IrEmitter
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.diag.InMemorySourceProvider
import top.wcpe.allincore.dsl.compiler.lex.LexException
import top.wcpe.allincore.dsl.compiler.lex.Lexer
import top.wcpe.allincore.dsl.compiler.parse.ParseException
import top.wcpe.allincore.dsl.compiler.parse.Parser
import top.wcpe.allincore.dsl.compiler.semantic.SemanticChecker
import top.wcpe.allincore.dsl.compiler.semantic.`native`.ClassRegistry
import top.wcpe.allincore.dsl.compiler.source.ScreenIdResolver
import top.wcpe.allincore.dsl.compiler.source.SourceFile

/**
 * 编译流水线：lex → parse → semantic → codegen 顺序串行。
 *
 * 异常约定：
 * - [LexException] / [ParseException] 携带结构化的 position 字段；message 已是纯净文本。
 * - semantic 阶段不抛异常，统一通过 [DiagnosticCollector] 收集；[CompileOptions.strict]
 *   仅控制是否尽早停止，分析结束后只要存在 ERROR 就跳过 codegen 并返回 [CompileResult.Failure]。
 *
 * 结构上拆为 [SingleSourceCompilation] 以降低主入口嵌套，并接入 master 侧
 * MaterialGraph / ShaderLayer 校验 pass；诊断列表使用 [DiagnosticCollector.stableAll]
 * 保持 KMP 跨端确定性。
 */
class CompilationPipeline(private val options: CompileOptions = CompileOptions()) {

    /** 完整编译入口：[SourceFile] → [CompileResult]。 */
    fun compile(source: SourceFile): CompileResult {
        val prepared = when (val result = CompilationSourceSet.prepare(emptyList(), emptyList(), source)) {
            is CompilationSourceSet.Result.Failure -> return CompileResult.Failure(result.diagnostics)
            is CompilationSourceSet.Result.Success -> result.sources.entry
        }
        return SingleSourceCompilation(options, prepared).compile()
    }

    /** 便利方法：直接传字符串 + 虚拟路径，包装为 [SourceFile] 后调用 [compile]。 */
    fun compile(text: String, name: String = "inline"): CompileResult =
        compile(SourceFile(name, text))
}

private class SingleSourceCompilation(
    private val options: CompileOptions,
    private val source: SourceFile,
) {
    // M5-f：注入 InMemorySourceProvider，使诊断附带源码片段与修复建议
    private val diag = DiagnosticCollector(
        sourceProvider = InMemorySourceProvider(source.path, source.text),
    )

    fun compile(): CompileResult {
        val screen = parse()
        val resolved = screen?.let(::expandAndResolve)
        return resolved?.let(::emit) ?: CompileResult.Failure(diag.stableAll())
    }

    private fun parse(): ScreenAst? {
        val tokens = try {
            Lexer(source.text, source.path).lex()
        } catch (exception: LexException) {
            diag.error(exception.error ?: DslError.E001_LEX_ERROR, exception.position, exception.message.orEmpty())
            return null
        }
        return try {
            Parser(tokens, ScreenIdResolver.derive(source.path), diag).parse()
        } catch (exception: ParseException) {
            // 优先透传 parser 抛出的细分错误码（DSL_E601 / E603 等）；为空时归并为 DSL_E002。
            val error = exception.error ?: DslError.E002_PARSE_ERROR
            diag.error(error, exception.position, exception.message.orEmpty())
            null
        }
    }

    private fun expandAndResolve(screen: ScreenAst): ScreenAst? {
        val registry = ComponentRegistry()
        ComponentCollectorPass(registry, diag).collectInline(screen.inlineComponents)
        val componentExpanded = continueWith(screen) {
            ComponentExpanderPass(registry, diag, options.knownComponentNames).expand(it)
        }
        // 先做 inventory 静态展开（一维），再做 slotGrid 静态展开（二维），最后进入语义检查，
        // 确保展开产物（box + slot）也走 ElementValidator。
        val inventoryExpanded = componentExpanded?.let { continueWith(it, InventoryExpanderPass(diag)::expand) }
        val gridExpanded = inventoryExpanded?.let { continueWith(it, SlotGridExpanderPass(diag)::expand) }
        val themeMerged = gridExpanded?.let { continueWith(it, ThemeMergePass(ThemeRegistry(), diag)::expand) }
        return themeMerged?.let(::resolveSymbols)
    }

    private fun resolveSymbols(screen: ScreenAst): ScreenAst? {
        ComponentTokenSymbolPass(diag).check(screen)
        if (hasStrictErrors()) return null
        val modifierExpanded = continueWith(screen, ModifierBundleExpandPass(diag)::expand)
        // master：material graph 成本/环检测 + shaderLayer program 符号校验
        modifierExpanded?.let {
            MaterialGraphValidationPass(diag).check(it)
            ShaderLayerProgramSymbolPass(diag, options.knownShaderPrograms).check(it)
        }
        if (hasStrictErrors()) return null
        // 编译期 style-class 样式层：把 styles {} 规则按元素 class 命中合并进 props，
        // 合并后 styleClasses / styles 被清空，绝不进入 IR。须在组件展开之后、语义检查之前。
        val styleApplied = modifierExpanded?.let { continueWith(it, StyleClassApplyPass()::apply) }
        // Stage 4：把内置宿主 object 的 MemberCall（如 net.request）lower 为专用 AST 节点
        val nativeResolved = styleApplied?.let {
            continueWith(it, NativeClassResolverPass(ClassRegistry.withBuiltins(), diag)::expand)
        }
        return nativeResolved?.let(::validateSemantics)
    }

    private fun validateSemantics(screen: ScreenAst): ScreenAst? {
        // Stage 5：await 目标校验（E603 等）
        val awaitChecked = continueWith(screen, AwaitTargetResolverPass(diag)::expand)
        awaitChecked?.let {
            SemanticChecker(
                diag,
                options.placeholderRegistry,
                options.knownComponentNames,
                options.crossFileTypes,
            ).check(it)
        }
        // 分析结束后只要存在 ERROR 就跳过 codegen（与 strict 无关）
        return awaitChecked?.takeUnless { diag.hasErrors() }
    }

    private fun continueWith(screen: ScreenAst, transform: (ScreenAst) -> ScreenAst): ScreenAst? {
        if (hasStrictErrors()) {
            return null
        }
        val transformed = transform(screen)
        return transformed.takeUnless { hasStrictErrors() }
    }

    private fun hasStrictErrors(): Boolean = options.strict && diag.hasErrors()

    private fun emit(screen: ScreenAst): CompileResult {
        val ir = IrEmitter(options.placeholderRegistry).emit(screen)
        return CompileResult.Success(ir, diag.stableAll(), kind = screen.kind)
    }
}
